import type { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '../icon/Icon';

/** Matches the Figma `Tone` property. */
export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

const accent = cva('w-1 self-stretch rounded-sm', {
  variants: {
    tone: {
      info: 'bg-status-info',
      success: 'bg-status-success',
      warning: 'bg-status-warning',
      danger: 'bg-status-danger',
    },
  },
  defaultVariants: { tone: 'info' },
});

/** An icon per tone, so the meaning does not rest on the accent colour alone. */
const TONE_ICON: Record<ToastTone, IconName> = {
  info: 'message',
  success: 'betslip-success',
  warning: 'warning',
  danger: 'danger',
};

export interface ToastProps {
  children: ReactNode;
  tone?: ToastTone;
  onDismiss?: () => void;
  className?: string;
}

export function Toast({ children, tone = 'info', onDismiss, className }: ToastProps) {
  return (
    <div
      // Failures need to interrupt; confirmations should wait for a pause in speech.
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={tone === 'danger' ? 'assertive' : 'polite'}
      className={cn(
        'flex min-h-15 items-center gap-3.5 rounded-md border border-border-default',
        'bg-bg-elevated p-4 text-text-primary shadow-lg',
        className,
      )}
    >
      <span aria-hidden className={accent({ tone })} />
      <Icon name={TONE_ICON[tone]} size={18} className="shrink-0 text-icon-muted" />
      <span className="flex-1 text-body-m">{children}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="shrink-0 rounded-sm p-1 text-icon-muted hover:bg-bg-surface hover:text-icon-default"
        >
          <Icon name="close" size={14} />
        </button>
      )}
    </div>
  );
}
